/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useAuth } from "auth/Auth";
import { normalizeListResponse } from "./listResponse";

/**
 * A single-record picker fed by a list endpoint (array or {items,...}).
 * The value is the record id. `initialLabel` shows the current record's name
 * before the options have loaded (read models carry `<thing>_name`).
 */
const ReferencePicker = ({
  apiPath,
  params,
  getLabel,
  value,
  onChange,
  label,
  required,
  disabled,
  initialLabel,
  size,
  helperText,
}) => {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const timer = useRef(null);
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    if (!apiPath || !axiosInstance || !isAuthReady || !isAuthenticated) return undefined;
    let cancelled = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(apiPath, {
          params: { page_size: 200, ...(params || {}), ...(search ? { search } : {}) },
        });
        if (!cancelled) setOptions(normalizeListResponse(res.data).items);
      } catch (error) {
        console.error(`Error loading options from ${apiPath}:`, error);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath, paramsKey, search, axiosInstance, isAuthReady, isAuthenticated]);

  const labelOf = (option) => {
    if (!option) return "";
    if (getLabel) return getLabel(option) || `#${option.id}`;
    return option.name || option.title || option.unit_number || option.reference || `#${option.id}`;
  };

  const selected = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;
    const found = options.find((o) => String(o.id) === String(value));
    if (found) return found;
    return { id: value, __placeholder: true, name: initialLabel || `#${value}` };
  }, [options, value, initialLabel]);

  return (
    <Autocomplete
      size={size}
      options={options}
      value={selected}
      disabled={disabled}
      loading={loading}
      getOptionLabel={(o) => (o?.__placeholder ? o.name : labelOf(o))}
      isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
      onChange={(_, option) => onChange(option ? option.id : null, option)}
      onInputChange={(_, text, reason) => {
        if (reason === "input") setSearch(text);
        if (reason === "clear") setSearch("");
      }}
      filterOptions={(x) => x}
      renderInput={(inputProps) => (
        <TextField
          {...inputProps}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...inputProps.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {inputProps.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

ReferencePicker.propTypes = {
  apiPath: PropTypes.string.isRequired,
  params: PropTypes.object,
  getLabel: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  initialLabel: PropTypes.string,
  size: PropTypes.string,
  helperText: PropTypes.string,
};

ReferencePicker.defaultProps = {
  params: null,
  getLabel: null,
  value: null,
  label: "",
  required: false,
  disabled: false,
  initialLabel: "",
  size: "medium",
  helperText: undefined,
};

export default ReferencePicker;
